import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { connect } from 'react-redux';
import { Drawer, Modal } from 'antd';
import Draggable from 'react-draggable';
import TelemedVideos from '../telemedVideos';
import ChatPanel from '../telemedCallChat';
import telemedActions from '../../redux/telemed/actions';
import usePrevious from '../../helpers/usePrevious';
import TitleBar from './TitleBar';
import ControlBar from './ControlBar';
import StageOverlay from './StageOverlay';
import MiniWidget from './MiniWidget';
import useCallClock from './useCallClock';
import {
  appointmentHourLabel,
  callStatus,
  clampPosition,
  initialsOf,
  patientDisplayName,
  viewportBounds,
  whatsappUrl,
} from './helpers';
import './style.css';

const MODAL_Z = 1750;
const MOBILE_MAX = 600;
const COMPACT_MAX = 937;
const ORIGIN = { x: 0, y: 0 };
const noop = () => {};

const getBreakpoint = () => ({
  isMobileWidth: window.innerWidth <= MOBILE_MAX,
  isCompactWidth: window.innerWidth <= COMPACT_MAX,
});

// No desktop largo o chat nasce aberto ao lado do vídeo; no resto ele é uma gaveta.
const chatStartsOpen = () => window.innerWidth > COMPACT_MAX;

const viewportSize = () => ({ width: window.innerWidth, height: window.innerHeight });

function modalWidthFor(isMobile, size) {
  if (isMobile) {
    return '100%';
  }
  if (size === 'compact') {
    return 440;
  }
  return 'min(1180px, calc(100vw - 48px))';
}

// O Draggable envolve o conteúdo sempre (desligado no celular): trocar a forma da
// árvore remontaria o <video>. Só a barra de título arrasta; as ações dela não.
function renderDraggable(node, {
  nodeRef,
  disabled,
  position,
  bounds,
  onStart,
  onStop,
}) {
  return (
    <Draggable
      nodeRef={nodeRef}
      disabled={disabled}
      handle=".tmd-titlebar"
      cancel=".tmd-titlebar-actions"
      position={position}
      bounds={bounds}
      onStart={onStart}
      onStop={onStop}
    >
      <div ref={nodeRef} className="tmd-drag">{node}</div>
    </Draggable>
  );
}

function TelemedModal({
  openTelemedModal,
  openUserMediaWebcam,
  userMediaAvailable,
  userMediaWebcamDisabled,
  createTelemedRoom,
  endTelemedRoom,
  roomId,
  peerConnectionConnected,
  dataChannelOpen,
  hasEverConnected,
  endingTelemedRoom,
  otherPeerEndedCall,
  ownMicMuted,
  remoteMicMuted,
  remoteCamOff,
  localStreamAvailable,
  telemedAppointmentData,
  telemedChatMessages,
  userType,
  userMediaError,
  noMediaDevices,
  noVideoDevices,
  noAudioDevices,
  connectionTimedOut,
  retryTelemedConnection,
  muteOwnMic,
  unmuteOwnMic,
  stopOwnWebcam,
  startOwnWebcam,
  patientsOnDB,
}) {
  const [breakpoint, setBreakpoint] = useState(getBreakpoint);
  const [size, setSize] = useState('normal');
  const [chatOpen, setChatOpen] = useState(chatStartsOpen);
  const [seenCount, setSeenCount] = useState(0);
  const [initiatedCall, setInitiatedCall] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [position, setPosition] = useState(ORIGIN);
  const [bounds, setBounds] = useState(undefined);
  const draggleRef = useRef(null);
  const positionRef = useRef(ORIGIN);
  const settledRef = useRef(false);
  positionRef.current = position;

  const isMobile = breakpoint.isMobileWidth;
  const open = Boolean(openTelemedModal && !minimized);
  const compact = isMobile || breakpoint.isCompactWidth || size === 'compact';
  const previousCompact = usePrevious(compact);
  const fullyConnected = Boolean(peerConnectionConnected && dataChannelOpen);
  const elapsedSeconds = useCallClock(fullyConnected, !openTelemedModal);
  const ending = endingTelemedRoom === true || Boolean(otherPeerEndedCall && hasEverConnected);
  const status = callStatus({
    fullyConnected,
    hasEverConnected,
    endingTelemedRoom,
    otherPeerEndedCall,
    connectionTimedOut,
    noMediaDevices,
    userMediaError,
    userMediaAvailable,
    elapsedSeconds,
  });

  const name = patientDisplayName(telemedAppointmentData);
  const initials = initialsOf(name);
  const patientId = telemedAppointmentData && telemedAppointmentData.user;
  const patient = patientId && patientsOnDB
    ? patientsOnDB.find((item) => item.id === patientId)
    : null;
  const avatar = (patient && patient.avatar) || null;
  const hourLabel = appointmentHourLabel(telemedAppointmentData);
  const whatsapp = whatsappUrl(telemedAppointmentData && telemedAppointmentData.userInfo);

  const incoming = (telemedChatMessages || [])
    .filter((message) => message.userType && message.userType !== userType)
    .length;
  const chatVisible = chatOpen && open;
  const unread = chatVisible ? 0 : Math.max(incoming - seenCount, 0);

  const measure = useCallback(() => {
    const node = draggleRef.current;
    if (!node) {
      return null;
    }
    const rect = node.getBoundingClientRect();
    if (!rect.width || !rect.height) {
      return null;
    }
    return rect;
  }, []);

  // Mantém o painel inteiro dentro da janela depois de qualquer mudança de tamanho.
  const clampToViewport = useCallback(() => {
    const rect = measure();
    if (!rect) {
      setPosition(ORIGIN);
      return;
    }
    const next = clampPosition({ rect, position: positionRef.current, viewport: viewportSize() });
    if (next.x !== positionRef.current.x || next.y !== positionRef.current.y) {
      setPosition(next);
    }
  }, [measure]);

  useEffect(() => {
    let frame = null;
    const handleResize = () => {
      setBreakpoint((prev) => {
        const next = getBreakpoint();
        if (prev.isMobileWidth === next.isMobileWidth
          && prev.isCompactWidth === next.isCompactWidth) {
          return prev;
        }
        return next;
      });
      if (frame !== null) {
        return;
      }
      frame = window.requestAnimationFrame(() => {
        frame = null;
        if (settledRef.current) {
          clampToViewport();
        }
      });
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (frame !== null) {
        window.cancelAnimationFrame(frame);
      }
    };
  }, [clampToViewport]);

  useEffect(() => {
    if (openTelemedModal) {
      openUserMediaWebcam();
    }
  }, [openTelemedModal, openUserMediaWebcam]);

  useEffect(() => {
    if (!openTelemedModal) {
      setInitiatedCall(false);
      setMinimized(false);
      setSeenCount(0);
      setPosition(ORIGIN);
      setSize('normal');
      setChatOpen(chatStartsOpen());
    }
  }, [openTelemedModal]);

  useEffect(() => {
    if (fullyConnected) {
      setInitiatedCall(true);
    }
  }, [fullyConnected]);

  useEffect(() => {
    if (openTelemedModal && !initiatedCall && localStreamAvailable) {
      createTelemedRoom(roomId);
    }
  }, [
    initiatedCall,
    openTelemedModal,
    createTelemedRoom,
    roomId,
    localStreamAvailable,
  ]);

  useEffect(() => {
    if (chatVisible) {
      setSeenCount(incoming);
    }
  }, [chatVisible, incoming]);

  // Ao virar gaveta o chat fecharia por cima do vídeo: começa fechado.
  useEffect(() => {
    if (compact && previousCompact === false) {
      setChatOpen(false);
    }
  }, [compact, previousCompact]);

  useEffect(() => {
    if (isMobile) {
      setPosition(ORIGIN);
    }
  }, [isMobile]);

  useEffect(() => {
    if (!open) {
      settledRef.current = false;
    }
  }, [open]);

  useLayoutEffect(() => {
    if (settledRef.current && !isMobile) {
      clampToViewport();
    }
  }, [size, chatOpen, compact, isMobile, clampToViewport]);

  const onDragStart = () => {
    const rect = measure();
    if (!rect) {
      return;
    }
    setBounds(viewportBounds({ rect, position: positionRef.current, viewport: viewportSize() }));
  };

  const onDragStop = (_event, data) => {
    const rect = measure();
    const dropped = { x: data.x, y: data.y };
    if (!rect) {
      setPosition(dropped);
      return;
    }
    setPosition(clampPosition({ rect, position: dropped, viewport: viewportSize() }));
  };

  const onAfterOpenChange = (visible) => {
    settledRef.current = visible;
    if (visible && !isMobile) {
      clampToViewport();
    }
  };

  const toggleMic = () => {
    if (ownMicMuted) {
      unmuteOwnMic();
      return;
    }
    muteOwnMic();
  };

  const toggleCam = () => {
    if (userMediaWebcamDisabled) {
      startOwnWebcam();
      return;
    }
    stopOwnWebcam();
  };

  const toggleSize = () => setSize((value) => (value === 'normal' ? 'compact' : 'normal'));
  const closeChat = () => setChatOpen(false);
  const endCall = () => endTelemedRoom(roomId);

  const openChatFromWidget = () => {
    setChatOpen(true);
    setMinimized(false);
  };

  const mediaReady = Boolean(userMediaAvailable && !userMediaError);
  const wrapClass = `tmd-wrap tmd-wrap-${size}${isMobile ? ' tmd-wrap-mobile' : ''}`;

  return (
    <div className={`tmd-root${isMobile ? ' tmd-root-mobile' : ''}`}>
      {openTelemedModal && minimized ? (
        <MiniWidget
          name={name}
          initials={initials}
          avatar={avatar}
          hourLabel={hourLabel}
          status={status}
          connected={fullyConnected}
          remoteCamOff={remoteCamOff}
          remoteMicMuted={remoteMicMuted}
          micMuted={ownMicMuted}
          camOff={userMediaWebcamDisabled}
          noAudioDevices={noAudioDevices}
          noVideoDevices={noVideoDevices}
          mediaReady={mediaReady}
          unread={unread}
          ending={ending}
          timedOut={Boolean(connectionTimedOut)}
          isMobile={isMobile}
          onToggleMic={toggleMic}
          onToggleCam={toggleCam}
          onOpenChat={openChatFromWidget}
          onRestore={() => setMinimized(false)}
          onRetry={() => retryTelemedConnection()}
          onEnd={endCall}
        />
      ) : null}
      <Modal
        wrapClassName={wrapClass}
        title={null}
        closable={false}
        footer={null}
        centered
        forceRender
        open={open}
        onCancel={() => setMinimized(true)}
        afterOpenChange={onAfterOpenChange}
        mask={isMobile}
        maskClosable={false}
        zIndex={MODAL_Z}
        width={modalWidthFor(isMobile, size)}
        styles={{ content: { padding: 0 }, body: { padding: 0 } }}
        modalRender={(node) => renderDraggable(node, {
          nodeRef: draggleRef,
          disabled: isMobile,
          position,
          bounds,
          onStart: onDragStart,
          onStop: onDragStop,
        })}
      >
        <div className="tmd-panel">
          <TitleBar
            name={name}
            initials={initials}
            avatar={avatar}
            hourLabel={hourLabel}
            status={status}
            whatsapp={whatsapp}
            canResize={!isMobile}
            size={size}
            onToggleSize={toggleSize}
            onMinimize={() => setMinimized(true)}
          />
          <div className="tmd-main">
            <div className="tmd-stage">
              <div className="tmd-videos">
                <TelemedVideos
                  hideControls
                  professional
                  isModal
                  isModalOpen={openTelemedModal}
                  userMediaAvailable={userMediaAvailable}
                  userMediaWebcamDisabled={userMediaWebcamDisabled}
                  accessUserMedia={openUserMediaWebcam}
                  showChatIcon={false}
                  showFullScreenIcon={false}
                  showChat={false}
                  setShowChat={noop}
                  setFullScreenMode={noop}
                  initiatedCall={initiatedCall}
                />
              </div>
              {status.note ? <span className="tmd-stage-note">{status.note}</span> : null}
              {fullyConnected ? <span className="tmd-pip-label">você</span> : null}
              <StageOverlay overlay={status.overlay} onRetry={() => retryTelemedConnection()} />
            </div>
            {compact ? (
              <Drawer
                rootClassName="tmd-chat-drawer"
                placement="bottom"
                height="70%"
                closable={false}
                open={chatOpen}
                mask={false}
                getContainer={false}
                styles={{ body: { padding: 0 } }}
              >
                <ChatPanel compact onClose={closeChat} />
              </Drawer>
            ) : null}
            {!compact && chatOpen ? <ChatPanel onClose={closeChat} /> : null}
          </div>
          <ControlBar
            micMuted={ownMicMuted}
            camOff={userMediaWebcamDisabled}
            noAudioDevices={noAudioDevices}
            noVideoDevices={noVideoDevices}
            mediaReady={mediaReady}
            chatOpen={chatOpen}
            unread={unread}
            ending={ending}
            connected={fullyConnected}
            onToggleMic={toggleMic}
            onToggleCam={toggleCam}
            onToggleChat={() => setChatOpen((value) => !value)}
            onEnd={endCall}
          />
        </div>
      </Modal>
    </div>
  );
}

export default connect((state) => ({
  openTelemedModal: state.Telemed.openTelemedModal,
  userMediaAvailable: state.Telemed.userMediaAvailable,
  userMediaWebcamDisabled: state.Telemed.userMediaWebcamDisabled,
  localStreamAvailable: state.Telemed.localStreamAvailable,
  roomId: state.Telemed.roomId,
  peerConnectionConnected: state.Telemed.peerConnectionConnected,
  dataChannelOpen: state.Telemed.dataChannelOpen,
  hasEverConnected: state.Telemed.hasEverConnected,
  endingTelemedRoom: state.Telemed.endingTelemedRoom,
  otherPeerEndedCall: state.Telemed.otherPeerEndedCall,
  ownMicMuted: state.Telemed.ownMicMuted,
  remoteMicMuted: state.Telemed.remoteMicMuted,
  remoteCamOff: state.Telemed.remoteCamOff,
  telemedAppointmentData: state.Telemed.telemedAppointmentData,
  telemedChatMessages: state.Telemed.telemedChatMessages,
  userType: state.Telemed.userType,
  userMediaError: state.Telemed.userMediaError,
  noMediaDevices: state.Telemed.noMediaDevices,
  noVideoDevices: state.Telemed.noVideoDevices,
  noAudioDevices: state.Telemed.noAudioDevices,
  connectionTimedOut: state.Telemed.connectionTimedOut,
  patientsOnDB: state.Contacts.patientsOnDB,
}), {
  ...telemedActions,
})(TelemedModal);
